import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';

export interface RankingItem {
  id: string;
  rank: number;
  product_id: string;
  product_name?: string;
  product_image?: string;
  product_price?: number;
  created_at: string;
  updated_at: string;
}

export interface UseRankingReturn {
  newArrivals: RankingItem[];
  topSelling: RankingItem[];
  loading: boolean;
  error: string | null;
  updateRanking: (table: 'newariverank' | 'topsellrank', productId: string, newRank: number) => Promise<void>;
  reorderRankings: (table: 'newariverank' | 'topsellrank', items: RankingItem[]) => Promise<void>;
  refreshData: () => Promise<void>;
  cleanupExcessProducts: (table: 'newariverank' | 'topsellrank') => Promise<void>;
  addProductToRanking: (table: 'newariverank' | 'topsellrank', productId: string) => Promise<void>;
  removeProductFromRanking: (table: 'newariverank' | 'topsellrank', productId: string) => Promise<void>;
}

export const useRanking = (): UseRankingReturn => {
  const [newArrivals, setNewArrivals] = useState<RankingItem[]>([]);
  const [topSelling, setTopSelling] = useState<RankingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRankingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch new arrivals with product details
      const { data: newArrivalsData, error: newArrivalsError } = await supabase
        .from('newariverank')
        .select(`
          *,
          products (
            name,
            main_image,
            price
          )
        `)
        .order('rank', { ascending: true });

      if (newArrivalsError) throw newArrivalsError;

      // Fetch top selling with product details
      const { data: topSellingData, error: topSellingError } = await supabase
        .from('topsellrank')
        .select(`
          *,
          products (
            name,
            main_image,
            price
          )
        `)
        .order('rank', { ascending: true });

      if (topSellingError) throw topSellingError;

      // Transform data
      const transformedNewArrivals = newArrivalsData?.map(item => ({
        id: item.id,
        rank: item.rank,
        product_id: item.product_id,
        product_name: item.products?.name || 'Unknown Product',
        product_image: item.products?.main_image || '',
        product_price: item.products?.price || 0,
        created_at: item.created_at,
        updated_at: item.updated_at,
      })) || [];

      const transformedTopSelling = topSellingData?.map(item => ({
        id: item.id,
        rank: item.rank,
        product_id: item.product_id,
        product_name: item.products?.name || 'Unknown Product',
        product_image: item.products?.main_image || '',
        product_price: item.products?.price || 0,
        created_at: item.created_at,
        updated_at: item.updated_at,
      })) || [];

      setNewArrivals(transformedNewArrivals);
      setTopSelling(transformedTopSelling);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching ranking data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateRanking = async (table: 'newariverank' | 'topsellrank', productId: string, newRank: number) => {
    try {
      const { error } = await supabase
        .from(table)
        .update({ rank: newRank, updated_at: new Date().toISOString() })
        .eq('product_id', productId);

      if (error) throw error;

      // Refresh data after update
      await fetchRankingData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update ranking');
      throw err;
    }
  };

  const reorderRankings = async (table: 'newariverank' | 'topsellrank', items: RankingItem[]) => {
    try {
      console.log(`Starting reorder for ${table} with ${items.length} items`);
      
      // First, set all ranks to negative values to avoid unique constraint conflicts
      console.log('Setting all ranks to negative values...');
      for (let i = 0; i < items.length; i++) {
        const { error } = await supabase
          .from(table)
          .update({ rank: -(i + 1), updated_at: new Date().toISOString() })
          .eq('id', items[i].id);

        if (error) {
          console.error(`Error setting negative rank for item ${items[i].id}:`, error);
          throw error;
        }
        
        // Add a small delay to make the process visible
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Now set the correct positive ranks
      console.log('Setting correct positive ranks...');
      for (let i = 0; i < items.length; i++) {
        const { error } = await supabase
          .from(table)
          .update({ rank: i + 1, updated_at: new Date().toISOString() })
          .eq('id', items[i].id);

        if (error) {
          console.error(`Error setting final rank for item ${items[i].id}:`, error);
          throw error;
        }
        console.log(`Successfully updated item ${items[i].id} to rank ${i + 1}`);
        
        // Add a small delay to make the process visible
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      console.log('All updates completed, refreshing data...');
      // Refresh data after reordering
      await fetchRankingData();
      console.log('Data refresh completed');
    } catch (err) {
      console.error('reorderRankings error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to reorder rankings';
      setError(errorMessage);
      throw err;
    }
  };

  const refreshData = async () => {
    await fetchRankingData();
  };

  const cleanupExcessProducts = async (table: 'newariverank' | 'topsellrank') => {
    try {
      const { error } = await supabase
        .from(table)
        .delete()
        .gt('rank', 10);

      if (error) throw error;

      // Refresh data after cleanup
      await fetchRankingData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cleanup excess products');
      throw err;
    }
  };

  const addProductToRanking = async (table: 'newariverank' | 'topsellrank', productId: string) => {
    try {

      // Check if product already exists in ranking
      const { data: existing, error: existingError } = await supabase
        .from(table)
        .select('id')
        .eq('product_id', productId)
        .single();

      if (existingError && existingError.code !== 'PGRST116') {
        throw existingError;
      }

      if (existing) {
        throw new Error('Product already exists in ranking');
      }

      // Get current max rank
      const { data: maxRankData, error: maxRankError } = await supabase
        .from(table)
        .select('rank')
        .order('rank', { ascending: false })
        .limit(1)
        .single();

      if (maxRankError && maxRankError.code !== 'PGRST116') {
        throw maxRankError;
      }

      const newRank = (maxRankData?.rank || 0) + 1;

      // Check if we're at the limit
      if (newRank > 10) {
        throw new Error('Cannot add more than 10 products to ranking');
      }

      // Add product to ranking
      const { error } = await supabase
        .from(table)
        .insert({
          product_id: productId,
          rank: newRank,
        });

      if (error) {
        console.error('Supabase insert error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      // Refresh data after adding
      await fetchRankingData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add product to ranking';
      setError(errorMessage);
      console.error('addProductToRanking error:', err);
      throw err;
    }
  };

  const removeProductFromRanking = async (table: 'newariverank' | 'topsellrank', productId: string) => {
    try {
      console.log(`Removing product ${productId} from ${table}`);
      
      // Get the rank of the product being removed
      const { data: productData, error: selectError } = await supabase
        .from(table)
        .select('rank')
        .eq('product_id', productId)
        .single();

      if (selectError) {
        console.error('Error selecting product:', selectError);
        throw selectError;
      }

      if (!productData) {
        throw new Error('Product not found in ranking');
      }

      const removedRank = productData.rank;
      console.log(`Product to remove has rank: ${removedRank}`);

      // Remove the product
      const { error: deleteError } = await supabase
        .from(table)
        .delete()
        .eq('product_id', productId);

      if (deleteError) {
        console.error('Error deleting product:', deleteError);
        throw deleteError;
      }

      console.log('Product deleted successfully');

      // Get all products with rank > removedRank and update them
      const { data: higherRankedProducts, error: fetchError } = await supabase
        .from(table)
        .select('id, rank')
        .gt('rank', removedRank)
        .order('rank', { ascending: true });

      if (fetchError) {
        console.error('Error fetching higher ranked products:', fetchError);
        throw fetchError;
      }

      // Update each product's rank
      for (const product of higherRankedProducts || []) {
        const { error: updateError } = await supabase
          .from(table)
          .update({ 
            rank: product.rank - 1,
            updated_at: new Date().toISOString()
          })
          .eq('id', product.id);

        if (updateError) {
          console.error(`Error updating product ${product.id}:`, updateError);
          throw updateError;
        }
      }

      console.log('All ranks updated successfully');

      // Refresh data after removal
      await fetchRankingData();
    } catch (err) {
      console.error('removeProductFromRanking error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove product from ranking';
      setError(errorMessage);
      throw err;
    }
  };

  useEffect(() => {
    fetchRankingData();
  }, []);

  return {
    newArrivals,
    topSelling,
    loading,
    error,
    updateRanking,
    reorderRankings,
    refreshData,
    cleanupExcessProducts,
    addProductToRanking,
    removeProductFromRanking,
  };
};
